"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface LocalizedItem {
  id: string;
  title: Record<Lang, string>;
  desc: Record<Lang, string>;
  to: string;
}
const industryList: LocalizedItem[] = [{
  id: "polymer",
  title: {
    en: "Polymer Filtration",
    zh: "聚合物过滤",
    es: "Filtración de polímeros",
    de: "Polymerfiltration",
    fr: "Filtration des polymères",
    pt: "Filtração de polímeros"
  },
  desc: {
    en: "High-strength wire cloth for continuous polymer melt screening.",
    zh: "用于连续聚合物熔体筛选的高强度金属布。",
    es: "Tela metálica de alta resistencia para el tamizado continuo de polímeros fundidos.",
    de: "Hochfestes Drahtgewebe für die kontinuierliche Siebung von Polymerschmelzen.",
    fr: "Toile métallique haute résistance pour le tamisage continu de polymères en fusion.",
    pt: "Tela metálica de alta resistência para peneiramento contínuo de polímeros fundidos."
  },
  to: "/PolymerFiltrationPage"
}, {
  id: "water-liquid",
  title: {
    en: "Water & Liquid Filtration",
    zh: "水与液体过滤",
    es: "Filtración de agua y líquidos",
    de: "Wasser- & Flüssigkeitsfiltration",
    fr: "Filtration d'eau et de liquides",
    pt: "Filtração de água e líquidos"
  },
  desc: {
    en: "Durable screening media for industrial water treatment and liquid separation.",
    zh: "用于工业水处理和液体分离的耐用筛网介质。",
    es: "Medios de tamizado duraderos para tratamiento de agua industrial y separación de líquidos.",
    de: "Langlebige Siebmedien für industrielle Wasseraufbereitung und Flüssigkeitstrennung.",
    fr: "Médias de tamisage durables pour le traitement de l'eau industrielle et la séparation des liquides.",
    pt: "Meios de peneiramento duráveis para tratamento de água industrial e separação de líquidos."
  },
  to: "/WaterLiquidFiltrationPage"
}, {
  id: "food-pharma",
  title: {
    en: "Food & Pharmaceutical",
    zh: "食品与制药",
    es: "Alimentación y farmacia",
    de: "Lebensmittel & Pharmazie",
    fr: "Agroalimentaire et pharmaceutique",
    pt: "Alimentos e produtos farmacêuticos"
  },
  desc: {
    en: "Hygienic mesh for sanitary batch processing and purity compliance.",
    zh: "用于卫生批处理和纯度合规的卫生网布。",
    es: "Malla higiénica para procesamiento sanitario por lotes y cumplimiento de pureza.",
    de: "Hygienisches Drahtgewebe für sterile Chargenverarbeitung und Reinheitskonformität.",
    fr: "Toile hygiénique pour le traitement sanitaire par lots et la conformité de pureté.",
    pt: "Malha higiênica para processamento sanitário por lotes e conformidade com pureza."
  },
  to: "/FoodAndPharmaceuticalFiltrationPage"
}, {
  id: "screening-separation",
  title: {
    en: "Screening & Separation",
    zh: "筛分与分离",
    es: "Tamizado y separación",
    de: "Siebung & Trennung",
    fr: "Tamisage et séparation",
    pt: "Peneiramento e separação"
  },
  desc: {
    en: "Plain and Dutch weaves for accurate sizing and particle separation.",
    zh: "用于精确分级和颗粒分离的平纹和荷兰编织。",
    es: "Tejidos lisos y holandeses para clasificación precisa y separación de partículas.",
    de: "Glatte Bindung und Tressengewebe für präzise Klassierung und Partikeltrennung.",
    fr: "Tissages unis et toiles reps pour un calibrage précis et la séparation des particules.",
    pt: "Tramas lisas e holandesas para classificação precisa e separação de partículas."
  },
  to: "/ScreeningAndSeparationHubPage"
}, {
  id: "gas-air",
  title: {
    en: "Gas & Air Filtration",
    zh: "气体与空气过滤",
    es: "Filtración de gases y aire",
    de: "Gas- & Luftfiltration",
    fr: "Filtration des gaz et de l'air",
    pt: "Filtração de gases e ar"
  },
  desc: {
    en: "Porous sintered and woven media for particulate capture in gas streams.",
    zh: "用于气流中颗粒捕获的多孔烧结和编织介质。",
    es: "Medios porosos sinterizados y tejidos para retención de partículas en corrientes de gas.",
    de: "Poröse Sinter- und Gewebemedien zur Partikelabscheidung in Gasströmen.",
    fr: "Médias frittés et tissés poreux pour la capture de particules dans les flux gazeux.",
    pt: "Meios porosos sinterizados e tecidos para captura de partículas em correntes de gás."
  },
  to: "/GasAndAirFiltrationPage"
}, {
  id: "chemical-process",
  title: {
    en: "Chemical & Process Filtration",
    zh: "化学与工艺过滤",
    es: "Filtración química y de procesos",
    de: "Chemie- & Prozessfiltration",
    fr: "Filtration chimique et de procédés",
    pt: "Filtração química e de processos"
  },
  desc: {
    en: "Corrosion-resistant wire cloth for harsh chemical processing.",
    zh: "用于恶劣化学工艺的耐腐蚀金属布。",
    es: "Tela metálica resistente a la corrosión para procesos químicos agresivos.",
    de: "Korrosionsbeständiges Drahtgewebe für anspruchsvolle chemische Prozesse.",
    fr: "Toile métallique résistante à la corrosion pour les procédés chimiques exigeants.",
    pt: "Tela metálica resistente à corrosão para processamento químico agressivo."
  },
  to: "/ChemicalFiltrationPage"
}, {
  id: "oil-fuel",
  title: {
    en: "Oil, Fuel & Petrochemical",
    zh: "石油、燃料与石化",
    es: "Petróleo, combustibles y petroquímica",
    de: "Öl, Kraftstoff & Petrochemie",
    fr: "Pétrole, carburants et pétrochimie",
    pt: "Pétrole, carburants et pétrochimie"
  },
  desc: {
    en: "Resilient mesh media for oil, fuel and petrochemical filtration duties.",
    zh: "用于石油、燃料和石油化工过滤任务的弹性网布。",
    es: "Medios de malla resistentes para tareas de filtración de petróleo, combustible y petroquímica.",
    de: "Robuste Maschenmedien für Filtrationsaufgaben in Öl, Kraftstoff und Petrochemie.",
    fr: "Médias filtrants résistants pour les applications de filtration pétrolière, carburant et pétrochimique.",
    pt: "Meios de malha resistentes para tarefas de filtração de óleo, combustível e petroquímica."
  },
  to: "/OilFuelFiltrationPage"
}];
export default function Industries() {
  const {
    lang
  } = useLanguage();
  const eyebrowLabel = pick(lang, {
    en: "Industry Sectors",
    zh: "行业领域",
    es: "Sectores industriales",
    de: "Industriebereiche",
    fr: "Secteurs industriels",
    pt: "Setores industriais"
  });
  const headingText = pick(lang, {
    en: "Application Areas",
    zh: "应用领域",
    es: "Áreas de aplicación",
    de: "Anwendungsbereiche",
    fr: "Domaines d'application",
    pt: "Áreas de aplicação"
  });
  const leadText = pick(lang, {
    en: "Precision woven wire mesh solutions engineered for industrial filtration and separation processes.",
    zh: "用于工业过滤、分离与筛分工艺的精密不锈钢编织网介质。",
    es: "Soluciones de malla de alambre tejida de precisión diseñadas para procesos industriales de filtración y separación.",
    de: "Präzisions-Drahtgewebelösungen für industrielle Filtrations- und Trennprozesse.",
    fr: "Solutions de toiles métalliques tissées de précision conçues pour les procédés industriels de filtration et de séparation.",
    pt: "Soluções de malha de arame tecida de precisão projetadas para processos industriais de filtração e separação."
  });
  return <section id="industrysolutionsgrid" data-nav="light" className="bg-background text-foreground border-b border-border mf-section">
      <div className="mf-container">
        {/* Header Block */}
        <Reveal>
          <div className="max-w-3xl mb-10 md:mb-12">
            <p className="mf-caption font-medium uppercase tracking-wider text-muted-foreground mb-3">
              {eyebrowLabel}
            </p>
            <h2 className="text-foreground font-default mf-h2">
              {headingText}
            </h2>
            <p className="mt-3 text-muted-foreground leading-relaxed mf-lead">
              {leadText}
            </p>
          </div>
        </Reveal>

        {/* 7-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {industryList.map((item, i) => {
          const currentTitle = pick(lang, item.title);
          const currentDesc = pick(lang, item.desc);
          return <Reveal key={item.id} delay={Math.min(i * 50, 300)} className="flex flex-col h-full">
                <Card className="group relative flex flex-col justify-between h-full bg-card text-card-foreground border border-border hover:border-primary/50 rounded-lg cursor-pointer p-0 gap-0">
                  {/* Overlay Link for accessible full-card navigation */}
                  <Link to={item.to} aria-label={currentTitle} className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" />

                  <CardHeader className="p-5 pb-3">
                    <CardTitle className="text-foreground leading-snug group-hover:text-primary transition-colors duration-200 mf-h3">
                      {currentTitle}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {currentDesc}
                    </CardDescription>
                  </CardHeader>

                  <CardFooter className="px-5 pt-2 pb-5 mt-auto flex items-center justify-end">
                    <ArrowRight className="size-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
                  </CardFooter>
                </Card>
              </Reveal>;
        })}
        </div>
      </div>
    </section>;
}